import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {TrainingMaterialsService} from '../../api/ms-filemanager/training-materials/training-materials.service';
import {LoadingService} from '../loading/loading-service/loading.service';
import {Subscription} from 'rxjs';
import {finalize, take, timeout} from 'rxjs/operators';

@Component({
    selector: 'app-training-materials-list',
    standalone: false,
    templateUrl: './training-materials-list.component.html',
    styleUrls: ['./training-materials-list.component.scss']
})
export class TrainingMaterialsListComponent implements OnInit, OnDestroy {


    fileNames: string[] = [];
    isLoading = false;
    hasError = false;

    private loadSubscription?: Subscription;
    private watchdogTimerId?: ReturnType<typeof setTimeout>;

    downloadInProgressFileName?: string;
    downloadFailedFileName?: string;

    constructor(
        private trainingMaterialsService: TrainingMaterialsService,
        private cdr: ChangeDetectorRef,
        private loadingService: LoadingService
    ) {}

    ngOnInit(): void {
        this.loadFiles();
    }

    loadFiles(): void {
        this.clearPendingOps();

        this.isLoading = true;
        this.hasError = false;
        this.loadingService.start();
        this.cdr.detectChanges();

        // Safety net for edge cases where browser/network stack never resolves the request.
        this.watchdogTimerId = setTimeout(() => {
            this.isLoading = false;
            this.hasError = true;
            this.fileNames = [];
            this.loadingService.stop();
            this.cdr.detectChanges();
        }, 20000);

        this.loadSubscription = this.trainingMaterialsService.listFiles().pipe(
            timeout(15000),
            take(1),
            finalize(() => {
                this.isLoading = false;
                this.clearWatchdog();
                this.loadingService.stop();
                this.cdr.detectChanges();
            })
        ).subscribe({
            next: (files) => {
                this.fileNames = files;
                this.hasError = false;
                this.cdr.detectChanges();
            },
            error: () => {
                this.hasError = true;
                this.fileNames = [];
                this.cdr.detectChanges();
            }
        });
    }

    downloadFile(fileName: string): void {
        if (this.downloadInProgressFileName) {
            return;
        }

        this.downloadFailedFileName = undefined;
        this.downloadInProgressFileName = fileName;
        this.cdr.detectChanges();

        this.trainingMaterialsService.downloadFile(fileName).pipe(
            timeout(20000),
            take(1),
            finalize(() => {
                this.downloadInProgressFileName = undefined;
                this.cdr.detectChanges();
            })
        ).subscribe({
            next: (response) => {
                const blob = response.body;
                if (!blob) {
                    this.downloadFailedFileName = fileName;
                    return;
                }

                const objectUrl = window.URL.createObjectURL(blob);
                const anchor = document.createElement('a');
                anchor.href = objectUrl;
                anchor.download = fileName;
                document.body.appendChild(anchor);
                anchor.click();
                anchor.remove();
                window.URL.revokeObjectURL(objectUrl);
            },
            error: () => {
                this.downloadFailedFileName = fileName;
            }
        });
    }

    ngOnDestroy(): void {
        this.clearPendingOps();
    }

    private clearPendingOps(): void {
        if (this.loadSubscription) {
            this.loadSubscription.unsubscribe();
            this.loadSubscription = undefined;
        }
        this.clearWatchdog();
    }

    private clearWatchdog(): void {
        if (this.watchdogTimerId) {
            clearTimeout(this.watchdogTimerId);
            this.watchdogTimerId = undefined;
        }
    }

    getExtension(fileName: string): string {
        const parts = fileName.split('.');
        return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
    }
}
